import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { Box, Spinner, Center, Button } from '@chakra-ui/react';
import { usePageTracking, useSessionTracking } from './services/analytics';
import { PermissionGuard } from './components/PermissionGuard';

// Eagerly loaded - needed immediately
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';

// Lazy loaded components - loaded on demand
const Dashboard = lazy(() => import('./views/admin/Dashboard'));
const Tenants = lazy(() => import('./views/admin/Tenants'));
const Payments = lazy(() => import('./views/admin/Payments'));
const Properties = lazy(() => import('./views/admin/Properties'));
const Reports = lazy(() => import('./views/admin/Reports'));
const Settings = lazy(() => import('./views/admin/Settings'));
const Expenses = lazy(() => import('./views/admin/Expenses'));
const MaintenanceManagement = lazy(() => import('./views/admin/MaintenanceManagement'));
const Monitoring = lazy(() => import('./views/admin/Monitoring'));

const SignIn = lazy(() => import('./views/auth/SignIn'));
const SignUp = lazy(() => import('./views/auth/SignUp'));
const ForgotPassword = lazy(() => import('./views/auth/ForgotPassword'));
const GmailCallback = lazy(() => import('./views/auth/GmailCallback'));
const ResetPassword = lazy(() => import('./views/auth/ResetPassword'));

// Tenant Portal Pages
const TenantLogin = lazy(() => import('./pages/tenant/TenantLogin'));
const TenantDashboard = lazy(() => import('./pages/tenant/TenantDashboard'));
const MakePayment = lazy(() => import('./pages/tenant/MakePayment'));
const PaymentHistory = lazy(() => import('./pages/tenant/PaymentHistory'));
const MaintenanceRequests = lazy(() => import('./pages/tenant/MaintenanceRequests'));
const LeaseDetails = lazy(() => import('./pages/tenant/LeaseDetails'));

// Phase 2-3 Feature Pages
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PaymentRemindersPage = lazy(() => import('./pages/PaymentRemindersPage'));
const CommissionPage = lazy(() => import('./pages/CommissionPage'));
const TaxReportingPage = lazy(() => import('./pages/TaxReportingPage'));
const TenantApplicationsPage = lazy(() => import('./pages/TenantApplicationsPage'));
const TenantChangeHistoryPage = lazy(() => import('./pages/TenantChangeHistoryPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));

const NotFound = lazy(() => import('./views/errors/NotFound'));
const ServerError = lazy(() => import('./views/errors/ServerError'));

const SupportWidget = lazy(() => import('./components/SupportWidget'));

// Marketing pages
const Home = lazy(() => import('./views/marketing/Home'));
const Landing = lazy(() => import('./views/marketing/Landing'));
const Terms = lazy(() => import('./views/marketing/Terms'));
const Privacy = lazy(() => import('./views/marketing/Privacy'));
const Security = lazy(() => import('./views/marketing/Security'));
const BookDemo = lazy(() => import('./views/marketing/BookDemo'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, userProfile } = useAuth();

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Center>
    );
  }

  if (!user) {
    return <Navigate to="/auth/signin" replace />;
  }

  // Redirect tenants to tenant portal - they should not access the main dashboard
  if (userProfile?.role === 'tenant') {
    console.warn('[ProtectedRoute] Tenant tried to access main portal, redirecting to tenant portal');
    return <Navigate to="/tenant/dashboard" replace />;
  }

  return <>{children}</>;
}

function ProtectedTenantRoute({ children }: { children: React.ReactNode }) {
  const tenantToken = localStorage.getItem('tenantToken');
  
  if (!tenantToken) {
    return <Navigate to="/tenant/login" replace />;
  }
  
  return <>{children}</>;
}

function App() {
  const { user } = useAuth();
  
  // Track page views on route changes
  usePageTracking();
  
  // Track user session
  useSessionTracking(user?.uid);

  return (
    <Box>
      <Suspense fallback={
        <Center h="100vh">
          <Spinner size="xl" color="brand.500" thickness="4px" />
        </Center>
      }>
        <Routes>
          {/* Marketing & Legal Pages - Public */}
          <Route path="/" element={<Home />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/book-demo" element={<BookDemo />} />

          {/* Auth Routes - Public */}
          <Route path="/auth/*" element={<AuthLayout />}>
            <Route path="signin" element={<SignIn />} />
            <Route path="signup" element={<SignUp />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="gmail-callback" element={<GmailCallback />} />
          </Route>

          {/* Tenant Portal Routes */}
          <Route path="/tenant/login" element={<TenantLogin />} />
          <Route 
            path="/tenant/dashboard" 
            element={
              <ProtectedTenantRoute>
                <TenantDashboard />
              </ProtectedTenantRoute>
            } 
          />
          <Route 
            path="/tenant/payments" 
            element={
              <ProtectedTenantRoute>
                <MakePayment />
              </ProtectedTenantRoute>
            } 
          />
          <Route 
            path="/tenant/payment-history" 
            element={
              <ProtectedTenantRoute>
                <PaymentHistory />
              </ProtectedTenantRoute>
            } 
          />
          <Route 
            path="/tenant/maintenance" 
            element={
              <ProtectedTenantRoute>
                <MaintenanceRequests />
              </ProtectedTenantRoute>
            } 
          />
          <Route 
            path="/tenant/maintenance/:requestId" 
            element={
              <ProtectedTenantRoute>
                <MaintenanceRequests />
              </ProtectedTenantRoute>
            } 
          />
          <Route 
            path="/tenant/lease" 
            element={
              <ProtectedTenantRoute>
                <LeaseDetails />
              </ProtectedTenantRoute>
            } 
          />

          {/* Protected Routes - Landlord Portal */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
          </Route>
          
          <Route
            path="/tenants"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Tenants />} />
          </Route>
          
          <Route
            path="/payments"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Payments />} />
          </Route>
          
          <Route
            path="/properties"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Properties />} />
          </Route>
          
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Expenses />} />
          </Route>

          <Route
            path="/maintenance"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<MaintenanceManagement />} />
          </Route>
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Reports />} />
          </Route>
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Settings />} />
          </Route>

          {/* Phase 2-3 Feature Routes */}
          <Route
            path="/features"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<FeaturesPage />} />
          </Route>

          <Route
            path="/payment-reminders"
            element={
              <ProtectedRoute>
                <PermissionGuard forbiddenRoles={['assistant']}>
                  <AdminLayout />
                </PermissionGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<PaymentRemindersPage />} />
          </Route>

          <Route
            path="/commission"
            element={
              <ProtectedRoute>
                <PermissionGuard forbiddenRoles={['assistant']}>
                  <AdminLayout />
                </PermissionGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<CommissionPage />} />
          </Route>

          <Route
            path="/tax-reporting"
            element={
              <ProtectedRoute>
                <PermissionGuard forbiddenRoles={['assistant']}>
                  <AdminLayout />
                </PermissionGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<TaxReportingPage />} />
          </Route>

          <Route
            path="/tenant-applications"
            element={
              <ProtectedRoute>
                <PermissionGuard forbiddenRoles={['assistant']}>
                  <AdminLayout />
                </PermissionGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<TenantApplicationsPage />} />
          </Route>

          <Route
            path="/tenant-history"
            element={
              <ProtectedRoute>
                <PermissionGuard forbiddenRoles={['assistant']}>
                  <AdminLayout />
                </PermissionGuard>
              </ProtectedRoute>
            }
          >
            <Route index element={<TenantChangeHistoryPage />} />
          </Route>

          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DocumentsPage />} />
          </Route>
          
          {/* Catch-all 404 for unmatched routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Global Support Widget - Unified Help & Feedback */}
        {user && <SupportWidget position="bottom-right" />}
      </Suspense>
    </Box>
  );
}

export default App;
